import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, BookOpen, Cpu, Compass, Globe, Send } from "lucide-react";

const rise = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } };

const education = [
  ["Master of Public Policy", "Hertie School of Governance, Berlin · DAAD Helmut Schmidt Scholar · 2013–2016"],
  ["Juris Doctor (master's degree equivalent)", "University of the Philippines · Justice Labrador Academic Excellence Scholar · 2005–2011"],
  ["BA Psychology", "University of the Philippines · 2001–2005"],
];

const certs = [
  ["Women's Leadership Coaching Programme", "Palena Neale, PhD · 2026"],
  ["UNSSC Leadership Pathway 360 · Perfect 5.0 in Managing for Innovation", "Talent Innovations · 2025"],
  ["Positive Intelligence", "Dilek Ciplak · 2024 to 2025"],
  ["AI Bootcamp for Learning Design", "Philippa Hardman · 2024"],
  ["Mastering Instructional Design Boot Camp", "Connie Malamed · 2021"],
  ["Futures Thinking Practitioner", "Metafuture School · Prof. Sohail Inayatullah (UNESCO) · 2021"],
  ["Certified Belbin Team Roles Trainer", "Belbin Associates · 2020"],
];

const skillGroups = [
  [BookOpen, "Learning & knowledge", ["Sustainable development learning", "Capacity development", "Instructional design", "Knowledge management", "Knowledge ecosystems"]],
  [Cpu, "Technology", ["AI integration", "Virtual reality (VR/XR)", "Microlearning design", "Digital transformation", "Learning platforms"]],
  [Compass, "Strategy", ["Systems thinking", "Futures thinking", "Strategic partnerships", "Programme design", "Procurement & vendors"]],
  [Globe, "Policy & comms", ["Public policy", "Legal affairs", "Strategic communications", "Stakeholder engagement", "UN 2.0 priorities"]],
];

const tags = ["UN System", "Sustainable Development", "Learning Innovation", "AI & VR", "Speaking Engagements", "Bonn · Berlin · Global"];

export default function ConnectTab() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="tab-panel" data-testid="tab-panel-connect">
      <div className="panel-wrap white">
        <div className="eyebrow">Get in touch</div>
        <h2 className="heading">Let's build something<br />that matters.</h2>
        <div className="connect-layout">
          <motion.div variants={rise} initial="hidden" animate="show">
            <p className="connect-intro">Open to collaborations, speaking engagements, knowledge partnerships, and senior opportunities at the intersection of sustainable development, learning innovation, and institutional transformation.</p>
            <a href="mailto:p.duman@unssc.org" className="connect-email" data-testid="connect-email"><Mail className="icon" />p.duman@unssc.org</a>

            <form className="contact-form" onSubmit={submit} data-testid="contact-form">
              <div className="mini-label" style={{ marginTop: "2rem" }}>Or send a message directly</div>
              <div className="cf-row">
                <input type="text" required placeholder="Your name" value={form.name} data-testid="contact-name-input"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input type="email" required placeholder="Your email" value={form.email} data-testid="contact-email-input"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <textarea required rows={5} placeholder="Your message" value={form.message} data-testid="contact-message-input"
                onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button type="submit" className="cf-submit" disabled={status === "sending"} data-testid="contact-submit-btn">
                <Send className="icon" /> {status === "sending" ? "Sending..." : "Send message"}
              </button>
              {status === "sent" && <p className="cf-status ok" data-testid="contact-success">Thank you — your message is on its way to Paulyn's inbox.</p>}
              {status === "error" && <p className="cf-status err" data-testid="contact-error">Something went wrong. Please try again or email directly.</p>}
            </form>

            <div className="c-tags" style={{ marginTop: "2.5rem" }}>
              {tags.map((t, i) => <span className="c-tag" key={i}>{t}</span>)}
            </div>
            <div className="mini-label">Education</div>
            <div className="cred-list">
              {education.map(([t, m], i) => (
                <div className="cred-item" key={i}><div className="cred-title">{t}</div><div className="cred-meta">{m}</div></div>
              ))}
            </div>
            <div className="mini-label" style={{ margin: "1.5rem 0 1rem" }}>Key certifications</div>
            <div className="cred-list">
              {certs.map(([t, m], i) => (
                <div className="cred-item" key={i}><div className="cred-title">{t}</div><div className="cred-meta">{m}</div></div>
              ))}
            </div>
          </motion.div>
          <motion.div variants={rise} initial="hidden" animate="show" transition={{ delay: 0.1 }}>
            <div className="mini-label">Areas of expertise</div>
            <div className="skill-cols">
              {skillGroups.map(([Icon, title, items], i) => (
                <div key={i}>
                  <div className="sk-group-title"><Icon className="icon" />{title}</div>
                  {items.map((s, j) => <div className="sk-item" key={j}>{s}</div>)}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
